import { useDroppable } from "@dnd-kit/core";
import {
  CheckCheck,
  ChevronsDownUp,
  ChevronsUpDown,
  Download,
  Upload,
  X
} from "lucide-react";
import { toast } from "sonner";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode
} from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { LoadingState } from "@/components/brand";
import { ReconnectingState } from "@/components/ui/reconnecting-state";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger
} from "@/components/ui/context-menu";
import {
  DropdownMenuContent,
  DropdownMenuItem
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { downloadFile } from "../../../api/OpenAIApi";
import type { ZipRequest } from "../../caseFiles/api/filesystemApi";
import { ClassifyFilesDialog } from "../../caseFiles/components/ClassifyFilesDialog";
import { useCaseTaxonomy } from "../../caseFiles/hooks/useCaseTaxonomy";
import { formatExhibitLabel } from "../../caseFiles/exhibitLabels";
import { nodeFilename, nodeTitle } from "../../caseFiles/nodeNames";
import {
  isPrivileged,
  type DocumentRole,
  type ExhibitScheme,
  type FsNode
} from "../../caseFiles/types";
import { draftTitle, formName } from "../workspace/workspaceModel";
import {
  WorkflowsMenuItem,
  WorkflowsSparkle
} from "../workflows/components/WorkflowsSparkle";
import { RoleReviewCard } from "./RoleReviewCard";
import {
  collectDroppedFiles,
  type CollectedFile
} from "../../caseFiles/upload/collectDroppedFiles";
import { PaneHeader } from "../components/PaneHeader";
import { PaneMaximizeButton } from "../layout/PaneMaximizeButton";
import { toggleFocusFromHeader } from "../layout/paneShortcuts";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { useExplorerDnd } from "../explorer/ExplorerDnd";
import { artifactKey, parseArtifactKey } from "../types";
import { sourceKey } from "./caseFileCopies";
import { summarize, zipName } from "./downloadModel";
import { ExhibitSchemeDialog } from "../../caseFiles/components/ExhibitSchemeDialog";
import { FileRow } from "./FileRow";
import { saveBlob, useZipDownload } from "./useZipDownload";
import {
  ancestorsOf,
  canDropInto,
  rangeBetween,
  stepRow,
  visibleRows,
  type TreeRow as GenericTreeRow
} from "./treeRows";
import { unusedCategoryNames } from "./categoryFolders";
import { isUnclassifiedFolder, unclassifiedCounts } from "./unclassified";
import { CaseFilesStart } from "./CaseFilesStart";
import { NewFolderMenu, NewFolderMenuItems } from "./NewFolderMenu";
import {
  CaseFilesMenuItems,
  type CaseFilesMenuActions,
  type CaseFilesMenuModel
} from "./caseFileMenus";
import { contextMenuKit, dropdownMenuKit } from "./menuKit";
import { useHeaderFit } from "./headerOverflow";
import {
  ExhibitFormatButton,
  ExhibitFormatSubmenu,
  OriginalNamesButton,
  OriginalNamesMenuItem
} from "./HeaderLabelControls";
import { TreeOverflowMenu } from "./TreeOverflowMenu";
import {
  SendIntakeIconButton,
  SendIntakeMenuItem,
  useSendIntake
} from "./sendIntake";
import { isOriginalNamesShortcut, useOriginalNames } from "./originalNames";
import { SelectionActions, type MoveTarget } from "./SelectionActions";
import { CaseUploadDialog } from "./UploadDialog";

const nodeTitleOf = (node: FsNode | undefined) =>
  node ? nodeTitle(node) : undefined;

type TreeRow = GenericTreeRow<FsNode>;

const ROOT_NAME = "Case Files";

type HeaderAction =
  | "upload"
  | "newFolder"
  | "exhibitFormat"
  | "originalNames"
  | "prepare"
  | "sendIntake"
  | "expand";
/**
 * The toolbar's actions, most important first. As the explorer narrows the
 * last ones move into "…" one by one; "…" and Maximize always stay.
 */
export const HEADER_PRIORITY: readonly HeaderAction[] = [
  "upload",
  "newFolder",
  "exhibitFormat",
  "originalNames",
  "prepare",
  "sendIntake",
  "expand"
];

type UploadTarget = {
  id: string | null;
  name: string;
  seeded: CollectedFile[] | null;
};

const IconButton: FC<{
  label: string;
  onClick: () => void;
  children: ReactNode;
}> = ({ label, onClick, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        aria-label={label}
        onClick={onClick}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

/**
 * The left-top pane: the case filesystem as a tree. Direct manipulation stays
 * here (upload, rename, move, include/exclude, delete); organising work goes
 * through the chat.
 */
export const CaseFilesTree: FC = () => {
  const {
    individualId,
    open,
    activeKey,
    requestMention,
    setSelectedNodeIds,
    pruneTabs,
    renameTab
  } = useCaseIde();
  const { fs, workspace } = useCaseData();
  const taxonomy = useCaseTaxonomy(individualId);
  const zip = useZipDownload();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const anchorRef = useRef<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [menuRow, setMenuRow] = useState<TreeRow | null>(null);
  const [rowMenuId, setRowMenuId] = useState<string | null>(null);
  const rowMenuIdRef = useRef<string | null>(null);
  rowMenuIdRef.current = rowMenuId;
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  const [classifying, setClassifying] = useState<string[] | null>(null);
  const [customLabels, setCustomLabels] = useState(false);
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);
  const [externalDrag, setExternalDrag] = useState(false);
  const [originalNames, setOriginalNames] = useOriginalNames();
  const sendIntake = useSendIntake();
  const seededRoot = useRef(false);
  const treeRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerFit = useHeaderFit({
    headerRef,
    priority: HEADER_PRIORITY,
    // "…" and Maximize.
    fixedCount: 2,
    titleKey: originalNames
  });
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const explorer = useExplorerDnd();
  const { setNodeRef: setRootDropRef } = useDroppable({
    id: "files:root",
    data: { tree: "files", root: true }
  });
  useEffect(
    () =>
      explorer.registerTree("files", {
        selection: () => selectedRef.current,
        expand: (parentId) =>
          setExpanded((current) => new Set([...current, parentId])),
        cancelRename: () => setRenamingId(null)
      }),
    [explorer]
  );

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!isOriginalNamesShortcut(e)) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']"))
        return;
      e.preventDefault();
      setOriginalNames(!originalNames);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [originalNames, setOriginalNames]);

  const rows = useMemo(
    () => visibleRows(fs.nodes, expanded, isUnclassifiedFolder),
    [fs.nodes, expanded]
  );
  const reviewCounts = useMemo(() => unclassifiedCounts(fs.nodes), [fs.nodes]);
  const byId = useMemo(
    () => new Map(fs.nodes.map((n) => [n.id, n])),
    [fs.nodes]
  );
  const folders = useMemo(
    () =>
      fs.nodes
        .filter((n) => n.kind === "folder" && !n.is_deleted)
        .sort((a, b) => a.path_cache.localeCompare(b.path_cache)),
    [fs.nodes]
  );
  const categories = taxonomy.data?.categories ?? [];
  const unusedAt = useCallback(
    (parentId: string | null) => {
      const siblings = fs.nodes
        .filter(
          (n) =>
            n.kind === "folder" && !n.is_deleted && n.parent_id === parentId
        )
        .map((n) => n.name);
      const parent = parentId
        ? fs.nodes.find((n) => n.id === parentId)
        : undefined;
      return unusedCategoryNames(
        categories,
        parent ? [...siblings, parent.name] : siblings
      );
    },
    [categories, fs.nodes]
  );

  // What copied files came from, for their tooltips.
  const sourceTitles = useMemo(() => {
    const out = new Map<string, string>();
    workspace.drafts.forEach((d) =>
      out.set(sourceKey("draft", d.draft_id), draftTitle(d))
    );
    workspace.forms.forEach((f) =>
      out.set(
        sourceKey("form", f.case_extend_uscis_filled_form_id),
        formName(f)
      )
    );
    return out;
  }, [workspace.drafts, workspace.forms]);

  // Open the top level once the tree first loads; keep tabs honest afterwards.
  useEffect(() => {
    if (fs.isLoading || seededRoot.current) return;
    seededRoot.current = true;
    setExpanded(
      new Set(
        fs.nodes
          .filter((n) => n.kind === "folder" && !n.parent_id)
          .map((n) => n.id)
      )
    );
  }, [fs.isLoading, fs.nodes]);
  useEffect(() => {
    // A failed load is not an empty case: the tabs stay until a real list says otherwise.
    if (fs.isLoading || fs.error) return;
    pruneTabs(
      (a) => (a.kind !== "file" && a.kind !== "card") || byId.has(a.id)
    );
    fs.nodes.forEach((n) => renameTab(`file:${n.id}`, nodeTitle(n)));
  }, [fs.isLoading, fs.error, fs.nodes, byId, pruneTabs, renameTab]);
  useEffect(() => {
    setSelectedNodeIds([...selected]);
  }, [selected, setSelectedNodeIds]);
  // A file opened from chat or a tab shows inside its expanded folders.
  const revealedKey = useRef<string | null>(null);
  useEffect(() => {
    const active = activeKey ? parseArtifactKey(activeKey) : null;
    if (active?.kind !== "file" || !byId.has(active.id)) return;
    if (revealedKey.current === activeKey) return;
    revealedKey.current = activeKey;
    const parents = ancestorsOf(fs.nodes, active.id);
    if (!parents.length) return;
    setExpanded((s) =>
      parents.every((id) => s.has(id)) ? s : new Set([...s, ...parents])
    );
  }, [activeKey, byId, fs.nodes]);

  const toggle = useCallback((id: string) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const openNode = useCallback(
    (node: FsNode) => {
      if (node.kind === "folder") {
        toggle(node.id);
        return;
      }
      open({ kind: "file", id: node.id, title: nodeTitle(node) });
    },
    [open, toggle]
  );

  const onRowClick = useCallback(
    (row: TreeRow, e: MouseEvent) => {
      const { id } = row.node;
      setFocusedId(id);
      if (e.shiftKey && anchorRef.current) {
        setSelected(new Set(rangeBetween(rows, anchorRef.current, id)));
      } else if (e.metaKey || e.ctrlKey) {
        setSelected((s) => {
          const next = new Set(s);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        anchorRef.current = id;
      } else {
        setSelected(new Set([id]));
        anchorRef.current = id;
        if (row.node.kind === "file")
          open({ kind: "file", id, title: nodeTitle(row.node) });
      }
    },
    [open, rows]
  );

  const selectVisible = useCallback(() => {
    const ids = rows.map((row) => row.node.id);
    setSelected(new Set(ids));
    anchorRef.current = ids[0] ?? null;
    if (ids[0]) setFocusedId(ids[0]);
  }, [rows]);

  const onSelectToggle = useCallback(
    (row: TreeRow, e: MouseEvent) => {
      const { id } = row.node;
      setFocusedId(id);
      if (e.shiftKey && anchorRef.current) {
        setSelected(new Set(rangeBetween(rows, anchorRef.current, id)));
        return;
      }
      setSelected((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      anchorRef.current = id;
    },
    [rows]
  );

  const onActionsOpenChange = useCallback((row: TreeRow, open: boolean) => {
    if (!open) {
      setRowMenuId((current) => (current === row.node.id ? null : current));
      setMenuRow((current) =>
        current?.node.id === row.node.id ? null : current
      );
      return;
    }
    setRowMenuId(row.node.id);
    setMenuRow(row);
    setFocusedId(row.node.id);
    setSelected((current) => {
      if (current.has(row.node.id)) return current;
      anchorRef.current = row.node.id;
      return new Set([row.node.id]);
    });
  }, []);

  const onKeyDown = useCallback(
    (row: TreeRow, e: KeyboardEvent) => {
      const { id } = row.node;
      const focusRow = (nextId: string | null) => {
        if (!nextId) return;
        setFocusedId(nextId);
        setSelected(new Set([nextId]));
        anchorRef.current = nextId;
        requestAnimationFrame(() => {
          treeRef.current
            ?.querySelector<HTMLElement>(`[data-node-id="${nextId}"]`)
            ?.focus();
        });
      };
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          focusRow(stepRow(rows, id, 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          focusRow(stepRow(rows, id, -1));
          break;
        case "ArrowRight":
          e.preventDefault();
          if (row.node.kind === "folder" && !row.expanded) toggle(id);
          else focusRow(stepRow(rows, id, 1));
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (row.node.kind === "folder" && row.expanded) toggle(id);
          else if (row.node.parent_id) focusRow(row.node.parent_id);
          break;
        case "Enter":
          e.preventDefault();
          openNode(row.node);
          break;
        case "F2":
          e.preventDefault();
          setRenamingId(id);
          break;
        case "Backspace":
        case "Delete":
          e.preventDefault();
          setConfirmDelete(selected.size ? [...selected] : [id]);
          break;
        default:
      }
    },
    [openNode, rows, selected, toggle]
  );

  // ---- mutations -----------------------------------------------------------
  const createFolder = useCallback(
    async (parentId: string | null, name = "New folder") => {
      const node = await fs.createFolder(name, parentId);
      if (!node) return;
      if (parentId)
        setExpanded(
          (s) => new Set([...s, parentId, ...ancestorsOf(fs.nodes, parentId)])
        );
      setSelected(new Set([node.id]));
      setFocusedId(node.id);
      if (name === "New folder") setRenamingId(node.id);
    },
    [fs]
  );
  const commitRename = useCallback(
    async (id: string, name: string) => {
      setRenamingId(null);
      await fs.rename(id, name);
      renameTab(`file:${id}`, name);
    },
    [fs, renameTab]
  );
  const deleteNodes = useCallback(
    async (ids: string[]) => {
      setConfirmDelete(null);
      await fs.remove(ids);
      setSelected((s) => new Set([...s].filter((x) => !ids.includes(x))));
    },
    [fs]
  );
  const setInclusion = useCallback(
    (ids: string[], included: boolean) =>
      Promise.all(
        ids
          .filter((id) => {
            const node = byId.get(id);
            return !(included && node && isPrivileged(node));
          })
          .map((id) => fs.setPacketInclusion(id, included))
      ),
    [fs, byId]
  );
  const setRole = useCallback(
    (ids: string[], role: DocumentRole) => {
      const files = ids.filter((id) => byId.get(id)?.kind === "file");
      if (files.length) void fs.setDocumentRole(files, role);
    },
    [fs, byId]
  );
  const setNumbering = useCallback(
    (ids: string[], numbered: boolean) =>
      Promise.all(ids.map((id) => fs.setExhibitNumbering(id, numbered))),
    [fs]
  );
  const openUpload = useCallback(
    (parentId: string | null, seeded: CollectedFile[] | null = null) => {
      const name = parentId ? byId.get(parentId)?.name ?? ROOT_NAME : ROOT_NAME;
      setUploadTarget({ id: parentId, name, seeded });
    },
    [byId]
  );

  // ---- downloads ------------------------------------------------------------
  const selectedFileIds = useMemo(
    () => [...selected].filter((id) => byId.get(id)?.kind === "file"),
    [selected, byId]
  );
  const selectionMoveTargets = useMemo<MoveTarget[]>(() => {
    if (selectedFileIds.length < 2) return [];
    const picked = selectedFileIds
      .map((id) => byId.get(id))
      .filter((n): n is FsNode => Boolean(n));
    const outside = (parent: string | null) =>
      !picked.every((n) => (n.parent_id ?? null) === parent);
    return [
      ...(canDropInto(fs.nodes, selectedFileIds, null) && outside(null)
        ? [{ id: null, label: ROOT_NAME }]
        : []),
      ...folders
        .filter(
          (f) => canDropInto(fs.nodes, selectedFileIds, f.id) && outside(f.id)
        )
        .map((f) => ({
          id: f.id,
          label: f.path_cache.replace(/^\//, "") || f.name
        }))
    ];
  }, [selectedFileIds, byId, fs.nodes, folders]);
  // "This Folder": the selected folder, or the folder the selected file is in.
  const focusFolder = useMemo(() => {
    const first = byId.get([...selected][0] ?? "");
    const folder = first
      ? first.kind === "folder"
        ? first
        : first.parent_id
        ? byId.get(first.parent_id)
        : undefined
      : undefined;
    return folder ? { id: folder.id, name: nodeTitle(folder) } : null;
  }, [selected, byId]);
  const summarizeRequest = useCallback(
    (request: ZipRequest) => summarize(fs.nodes, request),
    [fs.nodes]
  );
  const downloadZip = useCallback(
    (request: ZipRequest, folderName?: string) => {
      const summary = summarize(fs.nodes, request);
      void zip({
        tree: "files",
        request,
        name: zipName("files", request, { count: summary.files, folderName }),
        total: summary.files
      });
    },
    [fs.nodes, zip]
  );
  const downloadItems = useCallback(
    (ids: string[]) =>
      downloadZip({
        scope: "selected",
        node_ids: ids,
        keep_structure: true,
        prefix_labels: true
      }),
    [downloadZip]
  );
  const downloadOne = useCallback((node: FsNode) => {
    if (!node.storage_path) return;
    downloadFile(node.storage_path)
      .then((blob: Blob) => saveBlob(blob, nodeFilename(node)))
      .catch(() => toast.error("Couldn't download this file."));
  }, []);
  // ---- drag and drop (OS files) ---------------------------------------------
  const hasFiles = (e: DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const onExternalDrop = async (e: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setExternalDrag(false);
    const target =
      (e.target as HTMLElement).closest<HTMLElement>("[data-node-id]")?.dataset
        .nodeId ?? null;
    const targetNode = target ? byId.get(target) : null;
    const parentId = targetNode
      ? targetNode.kind === "folder"
        ? targetNode.id
        : targetNode.parent_id ?? null
      : null;
    const collected = await collectDroppedFiles(e.dataTransfer);
    if (collected.length) openUpload(parentId, collected);
  };

  const menuIds = menuRow
    ? selected.has(menuRow.node.id)
      ? [...selected]
      : [menuRow.node.id]
    : [];
  const menuNodes = menuIds
    .map((id) => byId.get(id))
    .filter(Boolean) as FsNode[];
  const allIncluded =
    menuNodes.length > 0 &&
    menuNodes.every((n) => n.packet_inclusion === "included");
  const menuFiles = menuNodes.filter((n) => n.kind === "file");
  const allPrivileged =
    menuFiles.length > 0 && menuFiles.length === menuNodes.length
      ? menuFiles.every(isPrivileged)
      : false;
  const menuRole =
    menuFiles.length > 0 &&
    menuFiles.every((n) => n.document_role === menuFiles[0].document_role)
      ? menuFiles[0].document_role ?? null
      : null;
  const allNumbered =
    menuNodes.length > 0 &&
    menuNodes.every((n) => n.exhibit_numbering === "numbered");
  // With nothing expanded, the button flips to Expand all (expands every folder).
  const allCollapsed = !folders.some((f) => expanded.has(f.id));
  const caseHasFiles = fs.nodes.some((n) => n.kind === "file");
  const expandLabel = allCollapsed ? "Expand all" : "Collapse all";
  const toggleExpandAll = () =>
    setExpanded(allCollapsed ? new Set(folders.map((f) => f.id)) : new Set());
  const rootFolderActions = {
    names: unusedAt(null),
    onCreateBlank: () => void createFolder(null),
    onCreateCategory: (name: string) => void createFolder(null, name)
  };
  const exhibitFormat = {
    scheme: fs.scheme,
    onSchemeChange: (next: ExhibitScheme) => void fs.saveScheme(next),
    onCustom: () => setCustomLabels(true),
    disabled: !caseHasFiles
  };
  const originalNamesToggle = {
    on: originalNames,
    onChange: setOriginalNames,
    disabled: !caseHasFiles
  };
  const shows = (id: HeaderAction) => headerFit.visible.has(id);
  const overflows = (id: HeaderAction) => !headerFit.visible.has(id);
  const menuModel: CaseFilesMenuModel = {
    menuRow,
    menuIds,
    menuNodes,
    menuFiles,
    folders,
    nodes: fs.nodes,
    allIncluded,
    allPrivileged,
    allNumbered,
    allCollapsed,
    menuRole,
    rootName: ROOT_NAME,
    selectAllDisabled: rows.length === 0
  };
  const menuActions: CaseFilesMenuActions = {
    selectAll: selectVisible,
    openNode,
    mention: (node) =>
      requestMention({ kind: "file", id: node.id, title: nodeTitle(node) }),
    unusedAt,
    createFolder: (parentId, name) => {
      void createFolder(parentId, name);
    },
    upload: (parentId) => openUpload(parentId),
    downloadFolder: (node) =>
      downloadZip(
        {
          scope: "folder",
          folder_id: node.id,
          keep_structure: true,
          prefix_labels: true
        },
        nodeTitle(node)
      ),
    rename: setRenamingId,
    move: (ids, parentId) => {
      void fs.move(ids, { target_parent_id: parentId });
    },
    downloadItems,
    downloadOne,
    setInclusion: (ids, included) => {
      void setInclusion(ids, included);
    },
    setRole,
    classify: setClassifying,
    setNumbering: (ids, numbered) => {
      void setNumbering(ids, numbered);
    },
    deleteNodes: setConfirmDelete,
    toggleExpandAll,
    refresh: () => {
      void fs.refresh();
    }
  };

  return (
    <div className="explorer-card flex h-full min-h-0 flex-col">
      <div className="relative shrink-0" ref={headerRef}>
        <PaneHeader
          title={ROOT_NAME}
          onDoubleClick={(e) => toggleFocusFromHeader(e, "files")}
          meta={
            originalNames ? (
              <Tag
                className="self-center pr-0.5"
                data-testid="original-names-chip"
              >
                Original Names
                <button
                  type="button"
                  aria-label="Show Exhibit Titles"
                  title="Show Exhibit Titles (⌥⇧N)"
                  onClick={() => setOriginalNames(false)}
                  className="flex size-4 items-center justify-center rounded-xs text-fg-3 hover:bg-fill-3 hover:text-fg"
                >
                  <X />
                </button>
              </Tag>
            ) : null
          }
          actions={
            <>
              {shows("prepare") ? <WorkflowsSparkle /> : null}
              {shows("upload") ? (
                <IconButton label="Upload" onClick={() => openUpload(null)}>
                  <Upload className="size-3.5" />
                </IconButton>
              ) : null}
              {shows("newFolder") ? (
                <NewFolderMenu {...rootFolderActions} />
              ) : null}
              {shows("sendIntake") ? (
                <SendIntakeIconButton target={sendIntake} />
              ) : null}
              {shows("expand") ? (
                <IconButton label={expandLabel} onClick={toggleExpandAll}>
                  {allCollapsed ? (
                    <ChevronsUpDown className="size-3.5" />
                  ) : (
                    <ChevronsDownUp className="size-3.5" />
                  )}
                </IconButton>
              ) : null}
              {shows("exhibitFormat") ? (
                <ExhibitFormatButton {...exhibitFormat} />
              ) : null}
              {shows("originalNames") ? (
                <OriginalNamesButton {...originalNamesToggle} />
              ) : null}
              <TreeOverflowMenu
                name={ROOT_NAME}
                testId="case-files-more"
                hasItems={caseHasFiles}
                onRefresh={() => void fs.refresh()}
                leading={
                  <DropdownMenuItem
                    disabled={rows.length === 0}
                    onSelect={selectVisible}
                    data-testid="case-files-more-select-all"
                  >
                    <CheckCheck />
                    Select All
                  </DropdownMenuItem>
                }
                download={{
                  tree: "files",
                  selectedIds: [...selected],
                  folder: focusFolder,
                  summarize: summarizeRequest,
                  onDownload: (download) => void zip(download)
                }}
                overflow={
                  headerFit.overflow.length ? (
                    <>
                      {overflows("prepare") ? <WorkflowsMenuItem /> : null}
                      {overflows("upload") ? (
                        <DropdownMenuItem
                          onSelect={() => openUpload(null)}
                          data-testid="case-files-more-upload"
                        >
                          <Upload />
                          Upload…
                        </DropdownMenuItem>
                      ) : null}
                      {overflows("newFolder") ? (
                        <NewFolderMenuItems {...rootFolderActions} />
                      ) : null}
                      {overflows("sendIntake") ? (
                        <SendIntakeMenuItem target={sendIntake} />
                      ) : null}
                      {overflows("expand") ? (
                        <DropdownMenuItem
                          onSelect={toggleExpandAll}
                          data-testid="case-files-more-expand"
                        >
                          {allCollapsed ? (
                            <ChevronsUpDown />
                          ) : (
                            <ChevronsDownUp />
                          )}
                          {expandLabel}
                        </DropdownMenuItem>
                      ) : null}
                      {overflows("exhibitFormat") ? (
                        <ExhibitFormatSubmenu {...exhibitFormat} />
                      ) : null}
                      {overflows("originalNames") ? (
                        <OriginalNamesMenuItem {...originalNamesToggle} />
                      ) : null}
                    </>
                  ) : null
                }
              />
              <PaneMaximizeButton focus="files" />
              <ExhibitSchemeDialog
                open={customLabels}
                onOpenChange={setCustomLabels}
                scheme={fs.scheme}
                scope="case"
                onSave={(next) => void fs.saveScheme(next)}
              />
            </>
          }
        />
      </div>
      <RoleReviewCard nodes={fs.nodes} onSetRole={setRole} />

      <ContextMenu
        onOpenChange={(open) => {
          if (!open && !rowMenuIdRef.current) setMenuRow(null);
        }}
      >
        <ContextMenuTrigger asChild>
          <div
            ref={(element) => {
              treeRef.current = element;
              setRootDropRef(element);
            }}
            role="tree"
            tabIndex={0}
            aria-label={ROOT_NAME}
            className={cn(
              "relative min-h-0 flex-1 overflow-y-auto py-1 [scrollbar-width:thin]",
              (externalDrag || explorer.isRootTarget("files")) &&
                "bg-accent-soft ring-1 ring-inset ring-accent-line"
            )}
            onDragOver={(e) => {
              if (hasFiles(e)) {
                e.preventDefault();
                setExternalDrag(true);
              }
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                setExternalDrag(false);
            }}
            onDrop={onExternalDrop}
            onContextMenu={(e) => {
              if (!(e.target as HTMLElement).closest("[data-node-id]"))
                setMenuRow(null);
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelected(new Set());
                setFocusedId(null);
              }
            }}
          >
            {fs.reconnecting && rows.length === 0 ? (
              <ReconnectingState
                onRetry={fs.refresh}
                className="absolute inset-0"
              />
            ) : fs.error ? (
              <div className="px-3 py-2 text-xs text-destructive">
                {fs.error}
              </div>
            ) : null}
            {fs.isLoading && rows.length === 0 ? (
              <LoadingState
                size="md"
                text={fs.reconnecting ? "Reconnecting…" : "Loading case files…"}
                className="absolute inset-0"
              />
            ) : null}
            {fs.loaded &&
            !fs.isLoading &&
            !fs.reconnecting &&
            rows.length === 0 ? (
              <CaseFilesStart
                onUpload={() => openUpload(null)}
                intake={sendIntake}
                dragging={externalDrag}
              />
            ) : null}
            {rows.map((row) => (
              <FileRow
                key={row.node.id}
                row={row}
                sourceTitle={
                  row.node.source_ref
                    ? sourceTitles.get(
                        sourceKey(
                          row.node.source_ref.kind,
                          row.node.source_ref.id
                        )
                      ) ?? null
                    : null
                }
                label={
                  row.node.packet_inclusion === "excluded"
                    ? null
                    : formatExhibitLabel(fs.labels[row.node.id], fs.scheme) ||
                      null
                }
                selected={
                  selected.has(row.node.id) ||
                  activeKey === artifactKey({ kind: "file", id: row.node.id })
                }
                picked={selected.has(row.node.id)}
                focused={focusedId === row.node.id}
                renaming={renamingId === row.node.id}
                reviewCount={reviewCounts.get(row.node.id)}
                dropHint={explorer.dropHintFor("files", row.node.id)}
                dragging={explorer.isDragging(row.node.id)}
                showOriginal={originalNames}
                onToggle={toggle}
                onClick={onRowClick}
                onDoubleClick={(r) => openNode(r.node)}
                onContextMenu={(r) => {
                  setRowMenuId(null);
                  setMenuRow(r);
                  setFocusedId(r.node.id);
                  if (!selected.has(r.node.id)) {
                    setSelected(new Set([r.node.id]));
                    anchorRef.current = r.node.id;
                  }
                }}
                onSelectToggle={onSelectToggle}
                actionsOpen={rowMenuId === row.node.id}
                onActionsOpenChange={onActionsOpenChange}
                actionsMenu={
                  rowMenuId === row.node.id ? (
                    <DropdownMenuContent
                      align="end"
                      className="w-56"
                      data-testid="file-row-actions-menu"
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      <CaseFilesMenuItems
                        kit={dropdownMenuKit}
                        model={menuModel}
                        actions={menuActions}
                      />
                    </DropdownMenuContent>
                  ) : null
                }
                onRenameCommit={commitRename}
                onRenameCancel={() => setRenamingId(null)}
                onKeyDown={onKeyDown}
              />
            ))}
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent
          className="w-56"
          data-testid="file-context-menu"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <CaseFilesMenuItems
            kit={contextMenuKit}
            model={menuModel}
            actions={menuActions}
          />
        </ContextMenuContent>
      </ContextMenu>

      {selectedFileIds.length > 1 ? (
        <div
          className="flex h-9 shrink-0 items-center gap-1.5 border-t border-line-1 bg-fill-1 pr-1.5 pl-3"
          data-testid="case-files-selection-bar"
        >
          <span className="min-w-0 flex-1 truncate text-sm text-fg-2">
            {selectedFileIds.length} Selected
          </span>
          <SelectionActions
            ids={selectedFileIds}
            moveTargets={selectionMoveTargets}
            onMove={(target) =>
              fs.move(selectedFileIds, { target_parent_id: target })
            }
            onAsk={() =>
              selectedFileIds.forEach((id) => {
                const node = byId.get(id);
                if (node)
                  requestMention({ kind: "file", id, title: nodeTitle(node) });
              })
            }
          />
          <Button
            variant="ghost"
            size="xs"
            onClick={selectVisible}
            data-testid="selection-select-all"
          >
            Select All
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </Button>
          <Button size="xs" onClick={() => downloadItems(selectedFileIds)}>
            <Download />
            Download
          </Button>
        </div>
      ) : null}

      {uploadTarget ? (
        <CaseUploadDialog
          open
          onOpenChange={(o) => !o && setUploadTarget(null)}
          targetName={uploadTarget.name}
          parentId={uploadTarget.id}
          initialCollected={uploadTarget.seeded}
          onAdded={(nodes) => {
            fs.addNodes(nodes);
            void fs.refresh();
            if (uploadTarget.id)
              setExpanded((s) => new Set([...s, uploadTarget.id as string]));
          }}
        />
      ) : null}

      {classifying ? (
        <ClassifyFilesDialog
          individualId={individualId}
          open
          onOpenChange={(o) => !o && setClassifying(null)}
          nodes={classifying
            .map((id) => byId.get(id))
            .filter((n): n is FsNode => !!n)}
          onSave={fs.setClassification}
        />
      ) : null}

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete{" "}
              {confirmDelete?.length === 1
                ? `"${nodeTitleOf(byId.get(confirmDelete[0])) ?? "item"}"`
                : `${confirmDelete?.length ?? 0} items`}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Folders are deleted with everything inside them. Exhibit labels
              renumber automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => confirmDelete && deleteNodes(confirmDelete)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
